import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { DatePicker } from '@dynamika-shared/ui/DatePicker/DatePicker';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { CalendarClearIcon } from '@dynamika-shared/ui/icons';
import { ClinicalTrialRequestType } from '@dynamika-shared/types/clinicalTrials';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { format } from 'date-fns';
import { useInventorySlice } from '@restore/inventory/inventory.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';

type ClinicalTrialCreateProps = {
  toggleVisibility: (isToggle: boolean) => void;
  isVisible?: boolean;
};
const MAX_PATIENT_AGE = 130;
const schema = yup.object().shape({
  name: yup
    .string()
    .max(320, 'Name should not exceed 320 characters')
    .required('Name is required'),
  sponsorId: yup.string().required('Sponsor is required'),
  startDate: yup.date().required('Start date is required'),
  endDate: yup
    .date()
    .required('End date is required')
    .min(yup.ref('startDate'), 'End date can\'t be before start date'),
  description: yup
    .string()
    .max(700, 'Description should not exceed 700 characters'),
});

export const ClinicalTrialCreate: FC<ClinicalTrialCreateProps> = ({
  toggleVisibility,
  isVisible,
}) => {
  const [createInProgress, createClinicalTrial] = useClinicalTrialSlice(
    (state, actions) => [
      state.clinicalTrialActionLoading,
      actions.createClinicalTrial,
    ]
  );

  const [sponsors] = useInventorySlice(state => [state.inventory.sponsors]);
  const { watch, setValue, trigger, reset, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      sponsorId: '',
      startDate: undefined,
      endDate: undefined,
      description: '',
    },
  });

  const { errors, isValid, isSubmitting, touchedFields } = formState;

  const onChangeField = async (
    fieldName: keyof ClinicalTrialRequestType,
    value: string | undefined
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof ClinicalTrialRequestType, value: string) => {
    if (touchedFields[fieldName]) {
      onChangeField(fieldName, value.trim());
    }
  };

  const submit = (data: ClinicalTrialRequestType) => {
    createClinicalTrial(data);
  };

  const sponsorsOptions = useMemo(() => {
    return sponsors?.map(({ id, name }) => ({
      id,
      name,
      value: id,
      label: name,
    }));
  }, [sponsors]);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);

  return (
    <Modal
      data-testid='addCTModal'
      width={464}
      onCancel={() => toggleVisibility(false)}
      onOk={handleSubmit(submit)}
      okProps={{
        htmlType: 'submit',
        disabled: !isValid || isSubmitting || createInProgress,
      }}
      cancelClassName={'w-[18rem] text-black'}
      okClassName={'w-[18rem]'}
      okText='Create'
      maskClosable={false}
      open={isVisible}
      title='Create new Clinical Trial'
    >
      <form name='create-clinical-trial' onSubmit={handleSubmit(submit)}>
        <hr className='my-3' />
        <div className='flex w-full flex-col gap-3 pr-2'>
          <Input
            required
            value={watch('name')}
            name='name'
            data-testid='name'
            onChange={e => onChangeField('name', e.target.value)}
            onBlur={e => onBlur('name', e.target.value)}
            errors={errors.name?.message}
            label={'Name'}
            placeholder={'Name this Clinical Trial'}
          />
          <Select
            required
            showSearch
            filterOption={(input, option) =>
              ((option?.label ?? '') as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            listHeight={160}
            errors={errors.sponsorId?.message}
            options={sponsorsOptions}
            value={watch('sponsorId')}
            name='sponsorId'
            onChange={e => onChangeField('sponsorId', e)}
            label={'Sponsor'}
            placeholder={'Sponsor'}
            data-testid='sponsorId'
          />
          <div>
            <span className={'text-1424 font-semibold'}>
              Start & End Dates <span className={'text-redOrange'}>*</span>
            </span>
            <div className={'mt-1.5 flex w-full justify-between gap-3'}>
              <DatePicker
                value={
                  watch('startDate')
                    ? new Date(watch('startDate') || new Date())
                    : null
                }
                onChange={(date: Date | null) =>
                  onChangeField(
                    'startDate',
                    date
                      ? format(date, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'')
                      : undefined
                  )
                }
                name='startDate'
                defaultValue={new Date()}
                format={{
                  format: 'DD.MM.YYYY',
                  type: 'mask',
                }}
                suffixIcon={<CalendarClearIcon className='h-2 w-2' />}
                disabledDate={current => {
                  current.setHours(0, 0, 0, 0);
                  // minDate - 10 years from now
                  const minDate = new Date();
                  minDate.setFullYear(minDate.getFullYear() - MAX_PATIENT_AGE);
                  if (current && current.getTime() < minDate.getTime()) {
                    return true;
                  }
                  const endDateValue = watch('endDate');
                  if (endDateValue) {
                    const endDate = new Date(endDateValue);
                    return current && current.getTime() > endDate.getTime();
                  }
                  return false;
                }}
                className={'h-6 grow rounded-3xl'}
                placeholder={'Start date'}
                data-testid='startDate'
              />

              <DatePicker
                value={
                  watch('endDate')
                    ? new Date(watch('endDate') || new Date())
                    : null
                }
                onChange={(date: Date | null) =>
                  onChangeField(
                    'endDate',
                    date
                      ? format(date, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'')
                      : undefined
                  )
                }
                disabledDate={current => {
                  current.setHours(0, 0, 0, 0);
                  return (
                    current &&
                    current.getTime() <
                      (watch('startDate')
                        ? new Date(watch('startDate') || new Date())
                        : new Date()
                      ).getTime()
                  );
                }}
                name='endDate'
                format={{
                  format: 'DD.MM.YYYY',
                  type: 'mask',
                }}
                suffixIcon={<CalendarClearIcon className='h-2 w-2' />}
                className={'h-6 grow rounded-3xl'}
                placeholder={'End date'}
                data-testid='endDate'
              />
            </div>
          </div>
          <TextArea
            className='!resize-none'
            rows={5}
            value={watch('description')}
            onChange={e => onChangeField('description', e.target.value)}
            onBlur={e => onBlur('description', e.target.value)}
            required={false}
            name={'description'}
            errors={errors.description?.message}
            data-testid='description'
            label={'Clinical Trial Description'}
            placeholder={'Clinical Trial Description'}
            maxLength={700}
          />
        </div>
      </form>
    </Modal>
  );
};
